import {inject, injectable} from "inversify";
import {CurrencyInterface, CurrencyMetaType} from "@/modules/action-points/interfaces";
import {Team} from "@/modules/team/entity";
import {EventEmitter} from "@/modules/base/events";
import BeforeCurrencyChangeEvent from "@/modules/action-points/ events/BeforeCurrencyChangeEvent";
import {GateTypeEnum} from "@/modules/action-points/enums";
import AfterCurrencyChangeEvent from "@/modules/action-points/ events/AfterCurrencyChangeEvent";

@injectable()
export default abstract class TeamFieldBasedCurrency implements CurrencyInterface {
  constructor(
    @inject(EventEmitter) protected em: EventEmitter,
  ) {}

  protected abstract getType(): GateTypeEnum;

  protected abstract getFieldName(): 'balance' | 'actionPointsScouting' | 'actionPointsNegotiating' | 'actionPointsTrainingGK' | 'actionPointsTrainingDF' | 'actionPointsTrainingMF' | 'actionPointsTrainingAT' | 'actionPointsCoins';

  async can(value: number): Promise<boolean> {
    return await this.get() >= value;
  }

  async get(): Promise<number> {
    const team = await Team.current();

    return team[this.getFieldName()];
  }

  async add(value: number, meta?: CurrencyMetaType): Promise<void> {
    const team = await Team.current();

    await this.em.emitAsync(new BeforeCurrencyChangeEvent(this.getType(), team, value));

    team[this.getFieldName()] += value;
    await team.save();

    await this.em.emitAsync(new AfterCurrencyChangeEvent(this.getType(), team, value));
  }

  async subtract(value: number, meta?: CurrencyMetaType): Promise<void> {
    const team = await Team.current();

    await this.em.emitAsync(new BeforeCurrencyChangeEvent(this.getType(), team, -value));

    team[this.getFieldName()] -= value;

    if (team[this.getFieldName()] < 0) {
      throw `Not sufficient currency '${this.getFieldName()}.`;
    }

    await team.save();

    await this.em.emitAsync(new AfterCurrencyChangeEvent(this.getType(), team, -value));
  }

  async forceSubtract(value: number, meta?: CurrencyMetaType): Promise<void> {
    const team = await Team.current();

    await this.em.emitAsync(new BeforeCurrencyChangeEvent(this.getType(), team, -value));

    team[this.getFieldName()] -= value;
    await team.save();

    await this.em.emitAsync(new AfterCurrencyChangeEvent(this.getType(), team, -value));
  }

  change(value: number, meta: CurrencyMetaType | undefined): Promise<void> {
    if (value === 0) {
      return Promise.resolve();
    }

    if (value < 0) {
      return this.subtract(-value, meta);
    }

    return this.add(value, meta);
  }
}
